import {
  Button,
  Datepicker,
  ErrorMessage,
  Spinner,
  Heading,
} from "@/common/components";
import { PrivateContext } from "@/routes/PrivateRoute";
import { useContext, useState, useEffect, useSelector } from "@/common/hooks";
import Projections from "./Projections";
import { useGetOwnershipInquiryMutation } from "@/service/OwnerService";
import dayjs from "dayjs";
import {
  setAccountValueDataSource,
  setAsOfBalanceAccountData,
  setHasSearchedForAsOfBalanceAccountData,
} from "@/store/ownersSlice";
import { amountFormatter } from "@/utils/Utilities";

const AccountDetails = ({ accountDetails, clickedOwnerData }) => {
  const {
    labels,
    productSponsorId,
    accessProfileId,
    dispatch,
    redirectTo,
    location,
  } = useContext(PrivateContext);

  const [isAccumulation, setIsAccumulation] = useState(false);
  const [showCurrPeriodGuaranteeValue, setShowCurrPeriodGuaranteeValue] =
    useState(false);
  const [asOfBalanceDate, setAsOfBalanceDate] = useState("");
  const [asOfAccountValueData, setAsOfAccountValueData] = useState();
  const [hasSearched, setHasSearched] = useState(false);
  const [isFixedProduct, setisFixedProduct] = useState(false);
  const [isSearchDateEmpty, setIsSearchDateEmpty] = useState(true);

  const isAccumulationValue =
    clickedOwnerData?.ownershipPhase === "A" ? true : false;

  const [getOwnershipInquiryApi, { isLoading: isFetching, error }] =
    useGetOwnershipInquiryMutation();

  const asOfBalanceAccountDataFromStore = useSelector(
    (state) => state?.ownersReducer?.asOfBalanceAccountData
  );
  const hasSearchedForAsOfBalanceAccountDataFromStore = useSelector(
    (state) => state?.ownersReducer?.hasSearchedForAsOfBalanceAccountData
  );

  const handleAsOfBalanceDate = (value) => {
    if (value) {
      const formattedValue = dayjs(value).format("MM/DD/YYYY");
      setAsOfBalanceDate(formattedValue);
      setIsSearchDateEmpty(false);
    } else {
      setIsSearchDateEmpty(true);
      setAsOfBalanceDate("");
    }
  };

  const fetchAsOfAccountValue = () => {
    dispatch(setHasSearchedForAsOfBalanceAccountData(true));
    dispatch(setAsOfBalanceAccountData(null));
    const {
      lineOfBusinessId,
      ownershipType,
      ownerTaxId,
      planId,
      product,
      recordkeeperRecordkeepingSystem,
    } = clickedOwnerData;

    const getOwnershipInquiryPayload = {
      lineOfBusinessId: lineOfBusinessId,
      ownershipTypeCode: ownershipType,
      ownerTaxId: ownerTaxId,
      planId: planId,
      externalProductId: product.externalId,
      recordKeeperName: recordkeeperRecordkeepingSystem.recordkeeperName,
      recordKeepingSystemName:
        recordkeeperRecordkeepingSystem.recordkeepingName,
      asOfBalanceDate: asOfBalanceDate,
      productSponsorId: productSponsorId,
      accessProfileId: accessProfileId,
    };

    getOwnershipInquiryApi(getOwnershipInquiryPayload)
      .unwrap()
      .then((res) => {
        dispatch(setAsOfBalanceAccountData(res));
      });
  };

  const currAccountValueData = [
    {
      label: labels?.ownerInquiry?.incomeBase,
      value: amountFormatter(accountDetails?.currentPeriodIncomeBaseAmount),
    },
    {
      label: labels?.ownerInquiry?.accountDetailsAccountValue,
      rawValue: accountDetails?.accountValueAmount,
      value: amountFormatter(accountDetails?.accountValueAmount),
      onClick: () => {
        if (accountDetails?.accountValueAmount > 0) {
          dispatch(setAccountValueDataSource("currAccountValue"));
          redirectTo("accountValueByVehicle");
        }
      },
    },
    !isAccumulation && {
      label: labels?.ownerInquiry?.returnOfPremium,
      value: amountFormatter(accountDetails?.returnOfPremiumAmount),
    },
    !isAccumulation && {
      label: labels?.ownerInquiry?.returnOfPremiumDeathBenefit,
      value: amountFormatter(accountDetails?.returnOfPremiumDeathBenefitAmount),
    },
    !isAccumulation && {
      label: labels?.ownerInquiry?.lifetimeConformingWithdrawals,
      value: amountFormatter(
        accountDetails?.lifetimeConformingWithdrawalAmount
      ),
    },
    !isAccumulation && {
      label: labels?.ownerInquiry?.lifetimeNonConformingWithdrawals,
      value: amountFormatter(
        accountDetails?.lifetimeNonConformingWithdrawalAmount
      ),
    },
  ].filter(Boolean);

  const currPeriodGuaranteeValue = [
    {
      label: labels?.ownerInquiry?.withdrawalPeriod,
      value: `${accountDetails?.withdrawalPeriodFromDate} to ${accountDetails?.withdrawalPeriodToDate}`,
    },
    {
      label: labels?.ownerInquiry?.guaranteedWithdrawalAmount,
      value: amountFormatter(accountDetails?.guaranteedWithdrawalAmount),
    },
    {
      label: labels?.ownerInquiry?.guaranteedWithdrawalPercent,
      value: accountDetails?.guaranteedWithdrawalPercent + "%",
    },
    {
      label: labels?.ownerInquiry?.amountWithdrawn,
      value: amountFormatter(accountDetails?.amountWithdrawn),
    },
    {
      label: labels?.ownerInquiry?.remainingWithdrawalAmount,
      value: amountFormatter(accountDetails?.remainingWithdrawalAmount),
    },
    {
      label: labels?.ownerInquiry?.excessWithdrawalAmount,
      value: amountFormatter(accountDetails?.excessWithdrawalAmount),
    },
  ];

  const nextPeriodGuaranteeValue = [
    {
      label: labels?.ownerInquiry?.withdrawalPeriod,
      value: `${accountDetails?.nextWithdrawalPeriodFromDate} to ${accountDetails?.nextWithdrawalPeriodToDate}`,
    },
    {
      label: labels?.ownerInquiry?.guaranteedWithdrawalAmount,
      value: amountFormatter(
        accountDetails?.nextPeriodGuaranteedAnnualWithdrawalAmount
      ),
    },
    {
      label: labels?.ownerInquiry?.incomeBase,
      value: amountFormatter(accountDetails?.nextPeriodIncomeBaseAmount),
    },
  ];

  const guaranteedBenefitsAtAge = [
    {
      label: labels?.ownerInquiry?.guaranteedMonthlyIncome,
      value: amountFormatter(accountDetails?.guaranteedMonthlyIncomeAmount),
    },
    {
      label: labels?.ownerInquiry?.sharePrice,
      value: amountFormatter(accountDetails?.sharePriceAmount),
    },
    {
      label: labels?.ownerInquiry?.noOfIncomeShares,
      value: accountDetails?.incomeShareCount,
    },
    {
      label: labels?.ownerInquiry?.cashOutValue,
      value: amountFormatter(accountDetails?.cashOutValueAmount),
    },
  ];

  useEffect(() => {
    setIsAccumulation(isAccumulationValue);
    setShowCurrPeriodGuaranteeValue(clickedOwnerData?.ownershipPhase === "A");
    setAsOfBalanceDate("");
    setIsSearchDateEmpty(true);
  }, [clickedOwnerData]);

  useEffect(() => {
    setisFixedProduct(clickedOwnerData?.product?.productType === "F");
  }, [location]);

  useEffect(() => {
    if (asOfBalanceAccountDataFromStore?.errors?.length === 0) {
      const asOfAccountValuesMain = [
        {
          label: labels?.ownerInquiry?.incomeBase,
          value: amountFormatter(
            asOfBalanceAccountDataFromStore?.currentPeriodIncomeBaseAmount
          ),
        },
        {
          label: labels?.ownerInquiry?.accountDetailsAccountValue,
          rawValue: asOfBalanceAccountDataFromStore?.accountValueAmount,
          value: amountFormatter(
            asOfBalanceAccountDataFromStore?.accountValueAmount
          ),
          onClick: () => {
            if (asOfBalanceAccountDataFromStore?.accountValueAmount > 0) {
              dispatch(setAccountValueDataSource("asOfDateAccountValue"));
              redirectTo("accountValueByVehicle");
            }
          },
        },
        {
          label: labels?.ownerInquiry?.guaranteedWithdrawalAmount,
          value: amountFormatter(
            asOfBalanceAccountDataFromStore?.guaranteedAnnualIncomeAmount
          ),
        },
        clickedOwnerData?.withdrawalElectionDate && {
          label: labels?.ownerInquiry?.amountWithdrawn,
          value: amountFormatter(
            asOfBalanceAccountDataFromStore?.amountWithdrawn
          ),
        },
        clickedOwnerData?.withdrawalElectionDate && {
          label: labels?.ownerInquiry?.remainingWithdrawalAmount,
          value: amountFormatter(
            asOfBalanceAccountDataFromStore?.remainingWithdrawalAmount
          ),
        },
        clickedOwnerData?.withdrawalElectionDate && {
          label: labels?.ownerInquiry?.excessWithdrawalAmount,
          value: amountFormatter(
            asOfBalanceAccountDataFromStore?.excessWithdrawalAmount
          ),
        },
      ].filter(Boolean);

      const asOfAccountValues = asOfAccountValuesMain.filter((item) => {
        const L = labels?.ownerInquiry ?? {};
        const awLabel = L?.amountWithdrawn;
        const rwLabel = L?.remainingWithdrawalAmount;
        const ewLabel = L?.excessWithdrawalAmount;

        const toNum = (v) => {
          if (v == null) return 0;
          if (typeof v === "number") return v;
          const n = Number(String(v).replace(/[^\d.-]/g, ""));
          return Number.isFinite(n) ? n : 0;
        };

        const aw = toNum(asOfBalanceAccountDataFromStore?.amountWithdrawn);
        const rw = toNum(
          asOfBalanceAccountDataFromStore?.remainingWithdrawalAmount
        );
        const ew = toNum(
          asOfBalanceAccountDataFromStore?.excessWithdrawalAmount
        );

        const isTargetLabel =
          item.label === awLabel ||
          item.label === rwLabel ||
          item.label === ewLabel;

        if (aw === 0 && rw === 0 && ew === 0 && isTargetLabel) {
          return false; // hide this row
        }

        return true; // keep everything else
      });

      setAsOfAccountValueData(asOfAccountValues);
    } else {
      setAsOfAccountValueData(null);
    }
  }, [asOfBalanceAccountDataFromStore]);

  useEffect(() => {
    setHasSearched(hasSearchedForAsOfBalanceAccountDataFromStore);
  }, [hasSearchedForAsOfBalanceAccountDataFromStore]);

  return (
    <div>
      {isFetching && <Spinner />}
      <ErrorMessage error={error} />

      {!isFixedProduct && (
        <div className="d-flex flex-column flex-md-row justify-content-between mt-3 column-gap-5 mb-5">
          <div className="d-flex flex-column flex-1">
            <div className="py-4 d-flex flex-column row-gap-2">
              <Heading
                text={labels?.ownerInquiry?.currAccountValue}
                wrapperClassNames="h4 border-bottom-1 pb-1 mb-0"
                title={false}
              />
              <span className="fw-lighter">
                {labels?.ownerInquiry?.asOfCloseOfBusiness}{" "}
                {accountDetails?.previousBusinessDay}
              </span>
            </div>
            {currAccountValueData.map((item, index) => (
              <div key={index} className="division">
                <span className="left">{item.label}</span>
                <span
                  className={`right ${
                    (item.onClick && item?.rawValue) > 0
                      ? "cursor-pointer link-color"
                      : ""
                  }`}
                  onClick={item.onClick ? item.onClick : null}
                >
                  {item.value}
                </span>
              </div>
            ))}
            {showCurrPeriodGuaranteeValue ? (
              ""
            ) : (
              <>
                <Heading
                  text={labels?.ownerInquiry?.currPeriodGuaranteeValue}
                  wrapperClassNames="h4 border-bottom-1 pb-1 mb-0 mt-5"
                  title={false}
                />
                {currPeriodGuaranteeValue.map((item, index) => (
                  <div key={index} className="division">
                    <span className="left">{item.label}</span>
                    <span className="right">{item.value}</span>
                  </div>
                ))}
                <Heading
                  text={labels?.ownerInquiry?.nextPeriodGuaranteeValue}
                  wrapperClassNames="h4 border-bottom-1 pb-1 mb-0 mt-5"
                  title={false}
                />
                {nextPeriodGuaranteeValue.map((item, index) => (
                  <div key={index} className="division">
                    <span className="left">{item.label}</span>
                    <span className="right">{item.value}</span>
                  </div>
                ))}
              </>
            )}
          </div>

          <div className="d-flex flex-column flex-1">
            <div className="py-4 d-flex flex-column row-gap-2">
              <Heading
                text={labels?.ownerInquiry?.asOfAccountValue}
                wrapperClassNames="h4 border-bottom-1 pb-1 mb-0"
                title={false}
              />
              {asOfBalanceAccountDataFromStore?.asOfBalanceDate && (
                <span className="fw-lighter">{`(${labels?.ownerInquiry?.asOfCloseOfBusiness}: ${asOfBalanceAccountDataFromStore?.asOfBalanceDate})`}</span>
              )}
              <Datepicker
                id="transactionDetailsAsOfBalanceDate"
                label={labels?.ownerInquiry?.asOfBalanceDate}
                value={asOfBalanceDate}
                change={handleAsOfBalanceDate}
                wrapperClassName="my-3 w-30"
                max={new Date()}
              />
              <Button
                disabled={isSearchDateEmpty}
                wrapperClassName="mb-3"
                text={
                  asOfAccountValueData || hasSearched
                    ? labels?.ownerInquiry?.searchAgain
                    : labels?.ownerInquiry?.search
                }
                click={fetchAsOfAccountValue}
              />
              {hasSearched && !isFetching ? (
                asOfAccountValueData ? (
                  <>
                    {asOfAccountValueData.map((item, index) => (
                      <div key={index} className="division">
                        <span className="left">{item.label}</span>
                        <span
                          className={`right ${
                            (item.onClick && item?.rawValue) > 0
                              ? "cursor-pointer link-color"
                              : ""
                          }`}
                          onClick={item.onClick ? item.onClick : null}
                        >
                          {item.value}
                        </span>
                      </div>
                    ))}
                  </>
                ) : (
                  <ErrorMessage
                    error={{
                      errorMessage:
                        labels?.ownerInquiry?.noAccountValueExistForDateError,
                    }}
                    errorClassName="rounded p-2 justify-content-center align-items-center"
                  />
                )
              ) : null}
            </div>
          </div>
        </div>
      )}

      {isFixedProduct && (
        <div className="d-flex flex-column mt-3">
          <div className="py-4 d-flex flex-column row-gap-2">
            <Heading
              text={`${labels?.ownerInquiry?.guaranteedBenefitsAge} ${accountDetails?.guaranteedBenefitsAge}`}
              wrapperClassNames="h4 border-bottom-1 pb-1 mb-0"
              title={false}
            />
            <span className="fw-lighter">
              {accountDetails?.previousBusinessDay &&
                labels?.ownerInquiry?.asOfCloseOfBusiness}{" "}
              {accountDetails?.previousBusinessDay}
            </span>
          </div>
          <div className="d-flex justify-content-between align-baseline">
            <div
              className={`${
                accountDetails?.guaranteedMonthlyIncomeAmount != 0
                  ? "col-9"
                  : "col-12"
              }`}
            >
              {guaranteedBenefitsAtAge.map((item, index) => (
                <div key={index} className="division">
                  <span className="left">{item.label}</span>
                  <span className="right">{item.value}</span>
                </div>
              ))}
            </div>
            {accountDetails?.guaranteedMonthlyIncomeAmount != 0 && (
              <div className="col-2">
                <div className="division border-bottom-0">
                  {amountFormatter(
                    accountDetails?.guaranteedMonthlyIncomeAmount * 12
                  )}
                  {labels?.ownerInquiry?.annually}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {isAccumulation && !isFixedProduct && (
        <Projections
          accountDetails={accountDetails}
          clickedOwnerData={clickedOwnerData}
        />
      )}
    </div>
  );
};

export default AccountDetails;
